/**
 * JobPostingForm Component
 *
 * Status: [IMPLEMENTED]
 * Task: T2.8.4 - Create job posting form
 * Task: T2.8.5 - Add form validation
 *
 * Form with company name input and job posting textarea.
 */

import React, { useState, useCallback } from 'react';

const MIN_JOB_POSTING_LENGTH = 100;
const MAX_JOB_POSTING_LENGTH = 50000;

const JobPostingForm = ({ onSubmit, disabled = false }) => {
  const [companyName, setCompanyName] = useState('');
  const [jobPosting, setJobPosting] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validation
  const validate = useCallback(() => {
    const newErrors = {};

    if (!companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!jobPosting.trim()) {
      newErrors.jobPosting = 'Job posting is required';
    } else if (jobPosting.trim().length < MIN_JOB_POSTING_LENGTH) {
      newErrors.jobPosting = `Job posting must be at least ${MIN_JOB_POSTING_LENGTH} characters`;
    } else if (jobPosting.length > MAX_JOB_POSTING_LENGTH) {
      newErrors.jobPosting = `Job posting must be less than ${MAX_JOB_POSTING_LENGTH.toLocaleString()} characters`;
    }

    return newErrors;
  }, [companyName, jobPosting]);

  // Handle field blur for validation
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const validationErrors = validate();
    setErrors(validationErrors);
  };

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({ companyName: true, jobPosting: true });

    // Validate
    const validationErrors = validate();
    setErrors(validationErrors);

    // If no errors, submit
    if (Object.keys(validationErrors).length === 0) {
      onSubmit({
        companyName: companyName.trim(),
        jobPosting: jobPosting.trim(),
      });
    }
  };

  const characterCount = jobPosting.length;
  const isValidLength = characterCount >= MIN_JOB_POSTING_LENGTH && characterCount <= MAX_JOB_POSTING_LENGTH;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Company Name */}
      <div>
        <label
          htmlFor="companyName"
          className="block text-sm font-medium text-slate-300 mb-2"
        >
          Company Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          id="companyName"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          onBlur={() => handleBlur('companyName')}
          disabled={disabled}
          placeholder="e.g., Famous AI"
          className={`w-full px-4 py-3 bg-slate-700 border rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed ${
            touched.companyName && errors.companyName
              ? 'border-red-500'
              : 'border-slate-600'
          }`}
        />
        {touched.companyName && errors.companyName && (
          <p className="mt-1 text-sm text-red-400">{errors.companyName}</p>
        )}
      </div>

      {/* Job Posting */}
      <div>
        <label
          htmlFor="jobPosting"
          className="block text-sm font-medium text-slate-300 mb-2"
        >
          Job Posting <span className="text-red-400">*</span>
        </label>
        <textarea
          id="jobPosting"
          value={jobPosting}
          onChange={(e) => setJobPosting(e.target.value)}
          onBlur={() => handleBlur('jobPosting')}
          disabled={disabled}
          placeholder="Paste the full job posting here..."
          rows={12}
          className={`w-full px-4 py-3 bg-slate-700 border rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed resize-y ${
            touched.jobPosting && errors.jobPosting
              ? 'border-red-500'
              : 'border-slate-600'
          }`}
        />
        {/* Character count */}
        <div className="mt-1 flex justify-between items-center">
          <p className={`text-sm ${
            touched.jobPosting && !isValidLength ? 'text-red-400' : 'text-slate-500'
          }`}>
            {characterCount.toLocaleString()} characters
            {characterCount > 0 && characterCount < MIN_JOB_POSTING_LENGTH && (
              <span> (need {MIN_JOB_POSTING_LENGTH - characterCount} more)</span>
            )}
          </p>
          {touched.jobPosting && errors.jobPosting && (
            <p className="text-sm text-red-400">{errors.jobPosting}</p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={disabled}
          className="w-full py-4 bg-sky-600 hover:bg-sky-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-lg flex items-center justify-center gap-2"
        >
          {disabled ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Generating...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Generate Resume</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default JobPostingForm;
